import { Injectable, OnDestroy } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DateTime } from "@lib/date-time";
import { BehaviorSubject, EMPTY, OverrideSubject, Subscription, combineLatest, interval, of, op } from "@lib/rxjs";
import { storageLocal } from "@lib/storage-local";
import { Device, User, Vehicle, VehicleLocationQuery, vantagePointMuckpileQuery } from "@models";
import { WebClient } from "./web-client";
import { FragQueryService } from "./frag-query.service";
import { WhereTable } from "@lib/where";
import { asFiniteNumber, isFiniteNumber } from "@lib/number";
import { FleetVehicleListItem } from "../../../shared-models/fleet-vehicle";
import { Response as WebClientResponse } from "@lib/web-client/response";
import { environment } from "@environment";

class LiveVehicleStreamRequestError extends Error {
	public constructor(public readonly status: number, message: string) {
		super(message);
	}
}

interface DurationOption {
	label: string;
	type: DateTime.Step;
	key?: string;
}

const durationOptions: DurationOption[] = [
	{
		label: 'Last 10 minutes',
		type: [10, 'minute'],
	},
	{
		label: 'Last 30 minutes',
		type: [30, 'minute'],
	},
	{
		label: 'Last hour',
		type: [1, 'hour'],
	},
	{
		label: 'Last 2 hour',
		type: [2, 'hour'],
	},
	{
		label: 'Last 3 Hours',
		type: [3, 'hour'],
	},
	{
		label: 'Last 6 Hours',
		type: [6, 'hour'],
	},
	{
		label: 'Last 12 Hours',
		type: [12, 'hour'],
	},
	{
		label: 'Last 24 Hours',
		type: [1, 'day'],
	}
];

for (const opt of durationOptions)
	opt.key = Array.isArray(opt.type) ? opt.type.join('') : opt.type;
const durationOptionMap = new Map(durationOptions.map((v) => [v.key, v]));

function liveTimeRange(step: DateTime.Step) {
	return interval(60 * 1000)
		.pipe(
			op.startWith(-1),
			op.map(() => {
				const end = new DateTime();
				const begin = end.clone().sub(step);
				return {
					begin,
					end,
				};
			}));
}

@Injectable({
	providedIn: "root",
})
export class VehicleQueryService implements OnDestroy {
	private readonly subscriptions = new Subscription();
	constructor(
		private readonly webClient: WebClient,
		private readonly user: User,
		public readonly fragQuery: FragQueryService,
		private readonly snackBar: MatSnackBar,
	) {
		this.init();
	}
    public readonly muckpiles$ = this.fragQuery.muckpiles$;
    public trigger$ = new BehaviorSubject<void>(null);
	public readonly vpMuckpileRefresh$ = new BehaviorSubject<void>(null);

	//==================== traceing page data variables ==============//
	public readonly defaultDevices$ = this.trigger$.pipe(
		op.switchMap(() => this.webClient.model.device.search$({
			sort: {
				clientId: '+',
				id: '+'
			}
		}))
	);

	public readonly defaultDeviceUuid$ = this.defaultDevices$.pipe(
        op.map((v) => v?.[0]?.uuid ?? null)
    );

	public readonly durationOptions = of(durationOptions);
	public readonly defaultDevice$ = this.defaultDevices$.pipe(op.map((v) => v?.[0] ?? null));
	public readonly siteId$ = this.fragQuery.siteId$;
	public readonly vehicles$ = this.siteId$.pipe(
		op.switchMap((siteId) => {
			const selectedSiteId = asFiniteNumber(siteId || this.user.siteId || 0);
			if (!selectedSiteId) {
				return of([] as FleetVehicleListItem[]);
			}

			return this.webClient.rest$({
				route: 'fleet/vehicles',
				method: 'get',
				body: {
					siteId: selectedSiteId,
					email: this.user.email,
					authToken: this.user.authToken
				}
			}).pipe(
				op.map(response => response.toRecord<FleetVehicleListItem[]>() ?? []),
				op.catchError(error => {
					console.error('Failed to fetch fleet vehicles:', error);
					this.snackBar.open('Unable to load fleet vehicles.', 'OK', { duration: 5000 });
					return of([] as FleetVehicleListItem[]);
				}),
				op.startWith([] as FleetVehicleListItem[])
			);
		}),
		op.shareReplay(1),
	);
	public readonly defaultSelectedVehicles$ = this.vehicles$;
	public liveVehicle$ = new BehaviorSubject<Vehicle[]>([]);
	public readonly sites$ = this.webClient.model.blastSite.search$({});
	public readonly deviceUuid$: OverrideSubject<string> = new OverrideSubject(this.defaultDeviceUuid$);
	public readonly devices$ = combineLatest([
		this.defaultDevices$,
		this.siteId$
	]).pipe(
		op.map(([devices, siteId]) => devices.filter(d => d.siteId === siteId) ?? null)
	);

	public defaultDurationOption = this.durationOptions.pipe(
		op.map(options => {
			const savedKey = storageLocal.get('vehicle-query-duration');
			const savedOption = savedKey ? durationOptionMap.get(savedKey) : undefined;

			return savedOption && options.includes(savedOption)
				? savedOption.key
				: '1hour';
		})
	);

	public readonly duration$ = new OverrideSubject(this.defaultDurationOption);
	private readonly playbackActive$ = new BehaviorSubject<boolean>(false);
	private liveVehicleStreamAbort: AbortController | null = null;
	private liveVehicleReconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private liveVehicleStreamVersion = 0;

	public readonly timeRange$ = combineLatest([
		this.duration$,
		this.playbackActive$,
	]).pipe(
		op.switchMap(([key, playbackActive]) => {
			if (playbackActive) return EMPTY;
			const duration = durationOptionMap.get(key) ?? durationOptionMap.get('1hour');
			return liveTimeRange(duration.type);
		}));

	/** Stops the moving live-query window so an active playback keeps fixed time bounds. */
	public setPlaybackActive(active: boolean): void {
		this.playbackActive$.next(active);
	}

	public readonly vehicleLocationForInterval$ = combineLatest(
		[
			this.timeRange$,
			this.defaultSelectedVehicles$,
		]
	).pipe(
		op.debounceTime(0),
		op.switchMap(([timeRange, vehicles]) => {
			const vehicleIds = vehicles.map(vehicle => vehicle.id).join(',');

			if (!vehicleIds) {
				return of(new WebClientResponse({
					begin: timeRange.begin,
					end: timeRange.end,
					tracePaths: []
				}));
			}

			const body: VehicleLocationQuery.Interval.Body & { siteId: number } = {
				email: this.user.email,
				authToken: this.user.authToken,
				vehicleIds,
				siteId: vehicles[0]?.siteId,
				begin: timeRange.begin,
				end: timeRange.end
			};

			return this.webClient
				.rest$({
					route: 'fleet/vehicle/location/interval',
					method: 'get',
					body,
				})
				.pipe(
					op.startWith(undefined),
					op.catchError(error => {
						console.error('Failed to load fleet history:', error);
						this.snackBar.open('Unable to load fleet location history.', 'OK', { duration: 5000 });
						return of(new WebClientResponse({
							begin: timeRange.begin,
							end: timeRange.end,
							tracePaths: []
						}));
					})
                )
		}),
		op.traverse((r) => r.toRecord<VehicleLocationQuery.Interval.Response>()),
		op.traverse((r) => ({
			begin: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(r.begin).replaceTimezone(this.user.timezoneOffset),
			end: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(r.end).replaceTimezone(this.user.timezoneOffset),
			tracePaths: r.tracePaths
		})),
		op.shareReplay(1)
	)

	private init() {
		this.subscriptions.add(this.devices$.subscribe((devices) => {
			let device: Device = null;
			if (devices) {
				const uuid = '' + storageLocal.get('frag-query-device-uuid');
				device = devices.find((device) => device.uuid === uuid);
				if (!device) device = devices[0];
			}
			if (device?.uuid)
				this.deviceUuid$.next(device.uuid);
		}));

		this.subscriptions.add(this.duration$.subscribe((v) => storageLocal.set('vehicle-query-duration', v)));

        this.subscriptions.add(this.vehicles$.subscribe(vehicles => this.updateLiveVehicles(vehicles)));
		this.initVehicleStream();
    }

	private initVehicleStream() {
        this.subscriptions.add(this.siteId$.pipe(
            op.map(siteId => this.getSelectedSiteId(siteId)),
            op.distinctUntilChanged()
        ).subscribe(siteId => {
            this.liveVehicle$.next([]);
			this.openLiveVehicleStream(siteId);
        }));
    }

	private openLiveVehicleStream(siteId: number): void {
		this.stopLiveVehicleStream();
		if (!siteId) return;

		const version = ++this.liveVehicleStreamVersion;
		const abort = new AbortController();
		this.liveVehicleStreamAbort = abort;
		void this.startLiveVehicleStream(siteId, version, abort);
	}

	private async startLiveVehicleStream(siteId: number, version: number, abort: AbortController): Promise<void> {
		try {
			const tokenUrl = new URL(
				'/fleet/vehicles/stream-token',
				environment.backendUrl || window.location.origin
			);
			const response = await fetch(tokenUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					siteId,
					email: this.user.email,
					authToken: this.user.authToken
				}),
				signal: abort.signal
			});
			if (!response.ok) {
				throw new LiveVehicleStreamRequestError(
					response.status,
					`Netra stream-token request returned HTTP ${response.status}.`
				);
			}
			const stream = await response.json() as { token?: unknown; streamUrl?: unknown };
			if (typeof stream?.token !== 'string' || typeof stream.streamUrl !== 'string') {
				throw new Error('Netra did not return a valid Fleet live-stream token.');
			}

			if (abort.signal.aborted || version !== this.liveVehicleStreamVersion) return;
			await this.consumeLiveVehicleStream(new URL(stream.streamUrl), siteId, version, abort, stream.token);
		} catch (error) {
			if (!abort.signal.aborted) {
				console.error('Unable to start Fleet live stream:', error);
				this.liveVehicle$.next([]);
				if (!this.isTerminalLiveStreamError(error)) {
					this.scheduleLiveVehicleReconnect(siteId, version);
				}
			}
		}
	}

	private async consumeLiveVehicleStream(
		url: URL,
		siteId: number,
		version: number,
		abort: AbortController,
		token: string
	): Promise<void> {
		let retry = true;
		try {
			const response = await fetch(url, {
				headers: {
					'Accept': 'text/event-stream',
					'Authorization': `Bearer ${token}`
				},
				signal: abort.signal
			});

			if (!response.ok || !response.body) {
				throw new LiveVehicleStreamRequestError(
					response.status,
					`Fleet live stream returned HTTP ${response.status}.`
				);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';

			while (!abort.signal.aborted) {
				const { done, value } = await reader.read();
				if (done) break;
				buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

				let boundary: number;
				while ((boundary = buffer.indexOf('\n\n')) >= 0) {
					const event = buffer.slice(0, boundary);
					buffer = buffer.slice(boundary + 2);
					const data = event.split('\n')
						.filter(line => line.startsWith('data:'))
						.map(line => line.slice(5).trimStart())
						.join('\n');

					if (!data) continue;

					try {
						const message = JSON.parse(data);
						if (message.error) {
							console.error('Fleet live update failed:', message.message ?? message.error);
							this.liveVehicle$.next([]);
						} else if (Array.isArray(message.vehicles)) {
							this.updateLiveVehicles(message.vehicles);
						}
					} catch {
						console.error('Received an invalid Fleet live-stream event.');
					}
				}
			}
		} catch (error) {
			if (!abort.signal.aborted) {
				console.error('Fleet live stream disconnected:', error);
				this.liveVehicle$.next([]);
				retry = !this.isTerminalLiveStreamError(error);
			}
		} finally {
			if (retry) this.scheduleLiveVehicleReconnect(siteId, version);
		}
	}

	private isTerminalLiveStreamError(error: unknown): boolean {
		return error instanceof LiveVehicleStreamRequestError
			&& [401, 403, 404].includes(error.status);
	}

	private scheduleLiveVehicleReconnect(siteId: number, version: number): void {
		if (siteId && !this.liveVehicleStreamAbort?.signal.aborted && version === this.liveVehicleStreamVersion) {
			this.liveVehicleReconnectTimer = setTimeout(() => this.openLiveVehicleStream(siteId), 5000);
		}
	}

	private stopLiveVehicleStream(): void {
		this.liveVehicleStreamVersion++;
		this.liveVehicleStreamAbort?.abort();
		this.liveVehicleStreamAbort = null;
		if (this.liveVehicleReconnectTimer !== null) clearTimeout(this.liveVehicleReconnectTimer);
		this.liveVehicleReconnectTimer = null;
	}

    private getSelectedSiteId(siteId: unknown): number {
        const selectedSiteId = asFiniteNumber(siteId || this.user.siteId || 0);
        return Number.isSafeInteger(selectedSiteId) && selectedSiteId > 0 ? selectedSiteId : 0;
    }

    private updateLiveVehicles(vehicles: Vehicle[]): void {
        this.liveVehicle$.next(vehicles.filter(vehicle =>
            isFiniteNumber(vehicle?.lastLat)
            && isFiniteNumber(vehicle?.lastLong)
            && Math.abs(vehicle.lastLat) <= 90
            && Math.abs(vehicle.lastLong) <= 180
        ));
    }

    public ngOnDestroy(): void {
		this.stopLiveVehicleStream();
        this.subscriptions.unsubscribe();
    }

	public readonly geofenceRecords$ = combineLatest([
		this.trigger$,
		this.timeRange$,
		this.muckpiles$,
		this.vehicles$,
		this.siteId$,
	]).pipe(
		op.switchMap(([_, timeRange, muckpiles, vehicles, siteId]) => {
			const beginTime = Math.floor(timeRange.begin.valueOf() / 1000);
			const endTime = Math.floor(timeRange.end.valueOf() / 1000);
			const selectedSiteId = asFiniteNumber(siteId || this.user.siteId || 0);
			const muckpileIds = muckpiles ? muckpiles?.map((m) => m.id) : []

			if (!selectedSiteId || muckpileIds.length === 0) {
				return of([]);
			}
			const whereClause: WhereTable = {
				time: [['>', beginTime], ['<', endTime]],
				siteId: selectedSiteId,
			};

			if (muckpileIds.length > 0) {
				whereClause['muckpileId'] = [['in', muckpileIds]];
			}

			return this.webClient.model.geofenceRecord.search$({
				where: whereClause,
				sort: { time: '+' }
			}).pipe(
				op.map(records => records.map(record => ({
					...record,
					time: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(record.time * 1000) : new DateTime(record.time * 1000).replaceTimezone(this.user.timezoneOffset),
					muckpileName: muckpiles?.find(m => m.id === record.muckpileId)?.name || 'Unknown Muckpile',
					vehicleName: vehicles?.find(v => v.externalId === record.vehicleId)?.name || 'Unknown Vehicle'
				})))
			);
		}),
		op.tap((records) => console.log("Enriched Geofence Records:", records)),
		op.shareReplay(1)
	);

	private getEmptyVpMuckpileResponse(): vantagePointMuckpileQuery.list.Response {
		return {
			siteId: 0,
			configFound: false,
			siteGuid: null,
			existingMuckpiles: [],
			nonExistingMuckpiles: [],
		};
	}

	public readonly vpMuckpileManager$ = combineLatest([
		this.siteId$,
		this.vpMuckpileRefresh$
	]).pipe(
		op.switchMap(([siteId]) => {
			const selectedSiteId = asFiniteNumber(siteId);
			if (!selectedSiteId) {
				return of(null);
			}

			const body: vantagePointMuckpileQuery.list.Body = {
				email: this.user.email,
				authToken: this.user.authToken,
				siteId: selectedSiteId,
			};

			return this.webClient.rest$({
				route: 'vp-muckpile-query/list',
				method: 'get',
				body,
			}).pipe(op.startWith(undefined));
		}),
		op.map((response) => response === undefined
			? undefined
			: response === null
				? this.getEmptyVpMuckpileResponse()
				: response.toRecord<vantagePointMuckpileQuery.list.Response>()),
		op.shareReplay(1)
	);

	public refreshVpMuckpileManager() {
		this.vpMuckpileRefresh$.next(null);
	}

	public async handleVpMuckpileAction(
		regionGuids: string[],
		action: 'add' | 'sync'
	) {
		const selectedSiteId = asFiniteNumber(this.siteId$.value);

		const cleanedRegionGuids = (Array.isArray(regionGuids) ? regionGuids : [])
			.map((regionGuid) => `${regionGuid || ''}`.trim())
			.filter((regionGuid) => regionGuid.length > 0);

		if (!selectedSiteId || cleanedRegionGuids.length === 0) {
			return null;
		}

		try {
			const response = await this.webClient.rest({
				route: 'vp-muckpile-query/add',
				method: 'post',
				body: {
					email: this.user.email,
					authToken: this.user.authToken,
					siteId: selectedSiteId,
					regionGuids: cleanedRegionGuids,
				} as vantagePointMuckpileQuery.add.Body,
			});

			const result = response.toRecord<vantagePointMuckpileQuery.add.Response>();

			if (result.success) {
				this.snackBar.open(`${result.added} VP muckpile(s) ${action}ed successfully.`, 'OK',
					{
						duration: 4000,
						panelClass: ['snackbar-success'],
					}
				);

				this.refreshVpMuckpileManager();
				this.fragQuery.trigger$.next();
			} else {
				this.snackBar.open(`No VP muckpiles were ${action}ed.`, 'OK',
					{ duration: 4000 }
				);
			}

			return result;
		} catch (error) {
			console.error(`Error ${action}ing VP muckpiles:`, error);

			this.snackBar.open(`Failed to ${action} VP muckpiles. Please try again.`, 'OK',
				{
					duration: 5000,
					panelClass: ['snackbar-error'],
				}
			);

			return null;
		}
	}

	public async removeVpMuckpiles(regionGuids: string[]) {
		const selectedSiteId = asFiniteNumber(this.siteId$.value);
		const cleanedRegionGuids = (Array.isArray(regionGuids) ? regionGuids : [])
			.map((regionGuid) => `${regionGuid || ''}`.trim())
			.filter((regionGuid) => regionGuid.length > 0);

		if (!selectedSiteId || cleanedRegionGuids.length === 0) {
			return null;
		}

		try {
			const response = await this.webClient.rest({
				route: 'vp-muckpile-query/remove',
				method: 'post',
				body: {
					email: this.user.email,
					authToken: this.user.authToken,
					siteId: selectedSiteId,
					regionGuids: cleanedRegionGuids,
				} as vantagePointMuckpileQuery.remove.Body,
			});
			const result = response.toRecord<vantagePointMuckpileQuery.remove.Response>();

			if (result.success) {
				this.snackBar.open(`${result.removed} VP muckpile(s) removed successfully.`, 'OK', {
					duration: 4000,
					panelClass: ['snackbar-success'],
				});
				this.refreshVpMuckpileManager();
				this.fragQuery.trigger$.next();
			} else {
				this.snackBar.open('No VP muckpiles were removed.', 'OK', {
					duration: 4000,
				});
			}

			return result;
		} catch (error) {
			console.error('Error removing VP muckpiles:', error);
			this.snackBar.open('Failed to remove VP muckpiles. Please try again.', 'OK', {
				duration: 5000,
				panelClass: ['snackbar-error'],
			});
			return null;
		}
	}
}
